/**
 * PlanPreview — full-screen preview shown right after "Generate Plan".
 * Matches the attached mobile mockup:
 *   • Dark hero image with title & tag row (module · level · duration · exercises · kcal)
 *   • "AI Plan" pill top-right
 *   • WARMUP · N and MAIN SETS · N sections, each with exercise rows
 *   • Blue "Start session" pill fixed at the bottom
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Play, Sparkles } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";

const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1517963879433-6ad2b056d712?w=1400&q=85&auto=format";

export default function PlanPreview() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    apiClient
      .get(`/workout/plans/${planId}`)
      .then((r) => setPlan(r.data))
      .catch(() => {
        toast.error("Plan not found");
        navigate("/workouts");
      });
  }, [planId, navigate]);

  if (!plan) {
    return (
      <div className="min-h-screen grid place-items-center" style={{ background: "#F4F3F1" }}>
        <div style={{ color: "#8A8884" }}>Loading plan…</div>
      </div>
    );
  }

  const warmup = plan.warmup || [];
  const main = plan.main_sets || [];
  const cooldown = plan.cooldown || [];
  const totalExercises = warmup.length + main.length + cooldown.length;
  const heroImage = main[0]?.thumbnail_url || warmup[0]?.thumbnail_url || HERO_FALLBACK;

  const tags = [
    (plan.module || plan.location || "gym").toUpperCase(),
    (plan.level || "intermediate").toUpperCase(),
    `${plan.duration_minutes || 45} min`,
    `${totalExercises} exercises`,
    `≈${plan.estimated_calories || 280} kcal`,
  ];

  return (
    <div className="min-h-screen" style={{ background: "#F4F3F1", paddingBottom: 96 }} data-testid="plan-preview">
      {/* Hero */}
      <div
        style={{
          position: "relative",
          height: 320,
          background: `linear-gradient(180deg, rgba(11,11,11,0.15) 0%, rgba(11,11,11,0.85) 100%), url(${heroImage}) center/cover no-repeat`,
        }}
      >
        <div
          style={{
            paddingTop: "calc(env(safe-area-inset-top) + 12px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "calc(env(safe-area-inset-top) + 12px) 16px 0",
          }}
        >
          <button
            onClick={() => navigate(-1)}
            data-testid="plan-back"
            aria-label="Back"
            className="w-10 h-10 grid place-items-center rounded-full"
            style={{ background: "rgba(255,255,255,0.14)", backdropFilter: "blur(8px)", border: 0, cursor: "pointer" }}
          >
            <ChevronLeft size={20} color="#fff" />
          </button>
          <div
            data-testid="plan-ai-badge"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 100,
              background: "#00D4FF",
              color: "#0C0C0B",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            <Sparkles size={14} /> AI Plan
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "16px 20px",
            color: "#fff",
          }}
        >
          <h1
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 800,
              fontSize: 32,
              letterSpacing: "-0.01em",
              lineHeight: 1.05,
            }}
            data-testid="plan-title"
          >
            {plan.plan_name}
          </h1>
          <div
            style={{
              marginTop: 10,
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 12,
              color: "rgba(255,255,255,0.85)",
            }}
            data-testid="plan-tags"
          >
            <span style={{ padding: "3px 8px", borderRadius: 6, background: "rgba(255,255,255,0.14)", fontWeight: 700, letterSpacing: "0.06em" }}>
              {tags[0]}
            </span>
            <span style={{ padding: "3px 8px", borderRadius: 6, background: "rgba(255,255,255,0.14)", fontWeight: 700, letterSpacing: "0.06em" }}>
              {tags[1]}
            </span>
            <span>{tags[2]}</span>
            <span style={{ opacity: 0.5 }}>·</span>
            <span>{tags[3]}</span>
            <span style={{ opacity: 0.5 }}>·</span>
            <span>{tags[4]}</span>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-2xl mx-auto" style={{ padding: "20px 16px" }}>
        {warmup.length > 0 && (
          <Section label="Warmup" count={warmup.length}>
            {warmup.map((ex, i) => (
              <ExerciseRow key={`w-${i}`} ex={ex} testid={`warmup-${i}`} />
            ))}
          </Section>
        )}
        {main.length > 0 && (
          <Section label="Main sets" count={main.length}>
            {main.map((ex, i) => (
              <ExerciseRow key={`m-${i}`} ex={ex} testid={`main-${i}`} />
            ))}
          </Section>
        )}
        {cooldown.length > 0 && (
          <Section label="Cooldown" count={cooldown.length}>
            {cooldown.map((ex, i) => (
              <ExerciseRow key={`c-${i}`} ex={ex} testid={`cooldown-${i}`} />
            ))}
          </Section>
        )}
      </div>

      {/* Sticky Start CTA */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          padding: "12px 16px calc(env(safe-area-inset-bottom) + 14px)",
          background: "linear-gradient(180deg, rgba(244,243,241,0) 0%, #F4F3F1 40%)",
          zIndex: 20,
        }}
      >
        <button
          onClick={() => navigate(`/workouts/session/${planId}`)}
          data-testid="plan-start-session"
          className="w-full max-w-2xl mx-auto flex items-center justify-center gap-2"
          style={{
            display: "flex",
            background: "#00D4FF",
            color: "#0C0C0B",
            borderRadius: 100,
            height: 56,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 700,
            fontSize: 16,
            border: 0,
            cursor: "pointer",
            boxShadow: "0 8px 20px rgba(0,212,255,0.35)",
          }}
        >
          <Play size={18} fill="#0C0C0B" /> Start session
        </button>
      </div>
    </div>
  );
}

function Section({ label, count, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div
        style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 11,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "#00B0D5",
          fontWeight: 800,
          marginBottom: 10,
          paddingLeft: 4,
        }}
      >
        {label} · {count}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{children}</div>
    </div>
  );
}

function ExerciseRow({ ex, testid }) {
  const thumb =
    ex.thumbnail_url ||
    "https://images.unsplash.com/photo-1517963879433-6ad2b056d712?w=400&q=80&auto=format";
  const target = `${ex.sets || 3} × ${ex.reps || "8-12"} · rest ${ex.rest_seconds || 60}s${
    ex.muscle_group ? " · " + ex.muscle_group : ""
  }`;
  return (
    <div
      data-testid={testid}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px 10px 10px",
        background: "#FFFFFF",
        borderRadius: 16,
        border: "1px solid #EEECE9",
        boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
      }}
    >
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: 12,
          overflow: "hidden",
          flexShrink: 0,
          background: "#111",
        }}
      >
        <img src={thumb} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 700,
            fontSize: 18,
            color: "#1A1A1A",
            lineHeight: 1.1,
          }}
        >
          {ex.name}
        </div>
        <div
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 12,
            color: "#8A8884",
            marginTop: 3,
          }}
        >
          {target}
        </div>
      </div>
      <ChevronRight size={18} color="#B8B4AE" />
    </div>
  );
}
